/**
 * 2026-04-25 Phase 32-C.8 — 장비 분해 확인 모달.
 *
 * 플레이어가 자발적으로 장비를 해체해 **장인의 재 + 제련석 + (각인 있었으면) 연민의 증표** 환원.
 * 제련 구간 3 진입 직전에 "안전하게 빼기" 선택지로 주로 쓰임.
 *
 * 예상 보상 공식은 백엔드와 동기화:
 *   ash  = max(1, enh/2) + (hadEngrave ? 1 : 0)
 *   stone = enh * 2   (enh>=7 이면 별철)
 *   pity  = hadEngrave ? 1 : 0
 */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { tokens } from "@/art/tokens";
import { PrimaryButton } from "@/components/Button/PrimaryButton";
import { salvageItem, type SalvageResult } from "@/api/endpoints/forge";
import { describeApiError } from "@/utils/errorMessages";
import type { InventoryItemView } from "@/api/types";
import { bracketShortLabel } from "@/utils/forge";

interface Props {
    runId: string;
    item: InventoryItemView;
    onClose: () => void;
    onComplete: (result: SalvageResult) => void;
}

export function SalvageConfirmModal({ runId, item, onClose, onComplete }: Props) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<SalvageResult | null>(null);

    const enh = item.enhancement ?? 0;
    const hadEngrave = !!item.engraveTier;
    const estAsh = Math.max(1, Math.floor(enh / 2)) + (hadEngrave ? 1 : 0);
    const estStone = enh * 2;
    const stoneLabel = enh >= 7 ? "별철" : "기본 제련석";
    const estPity = hadEngrave ? 1 : 0;
    const itemName = item.definition?.name ?? "장비";

    const doSalvage = async () => {
        setBusy(true);
        try {
            const res = await salvageItem(runId, item.instanceId);
            setResult(res);
        } catch (e) {
            const d = describeApiError(e);
            setError(`${d.title}: ${d.message}`);
        } finally {
            setBusy(false);
        }
    };

    const finish = () => {
        if (result) onComplete(result);
        onClose();
    };

    return (
        <div style={overlayStyle} onClick={onClose}>
            <motion.div
                onClick={e => e.stopPropagation()}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                style={modalStyle}
            >
                <AnimatePresence mode="wait">
                    {!result ? (
                        <motion.div key="confirm" exit={{ opacity: 0 }}>
                            <h2 style={titleStyle}>🪓 분해할까요?</h2>
                            <p style={{ fontSize: 14, opacity: 0.85, marginBottom: 16, lineHeight: 1.6 }}>
                                <strong>{itemName}</strong> (+{enh}, {bracketShortLabel(enh)})
                                {hadEngrave && <> · 각인 <strong>{item.engraveId}</strong> 새겨짐</>}
                                {" 을(를) 해체합니다."}
                            </p>

                            <div style={rewardBox}>
                                <div style={rewardTitle}>예상 회수</div>
                                <div style={rewardRow}>
                                    <span style={rewardIcon}>🪨</span>
                                    <span>장인의 재 × <strong>{estAsh}</strong></span>
                                </div>
                                {estStone > 0 && (
                                    <div style={rewardRow}>
                                        <span style={rewardIcon}>⛏</span>
                                        <span>{stoneLabel} × <strong>{estStone}</strong></span>
                                    </div>
                                )}
                                {estPity > 0 && (
                                    <div style={rewardRow}>
                                        <span style={rewardIcon}>🕊</span>
                                        <span>연민의 증표 × <strong>{estPity}</strong></span>
                                    </div>
                                )}
                            </div>

                            <p style={warningText}>
                                ⚠ 장비는 <strong>영원히 사라집니다</strong>.
                                장착 중이었다면 자동으로 해제됩니다.
                            </p>

                            {error && <p style={{ color: tokens.color.burgundy, fontSize: 12 }}>{error}</p>}

                            <div style={buttonRow}>
                                <button style={secondaryBtn} onClick={onClose} disabled={busy}>
                                    다시 생각한다
                                </button>
                                <button
                                    style={{
                                        ...secondaryBtn,
                                        borderColor: tokens.color.burgundy,
                                        color: tokens.color.burgundy,
                                    }}
                                    onClick={doSalvage}
                                    disabled={busy}
                                >
                                    {busy ? "해체 중…" : "🪓 해체한다"}
                                </button>
                            </div>
                        </motion.div>
                    ) : (
                        <motion.div key="result" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                            <h2 style={titleStyle}>🪓 해체 완료</h2>
                            {/* 2026-04-25: 플레이버 한 줄 제거 — 회수 결과 박스가 자명. */}
                            <div style={rewardBox}>
                                <div style={rewardTitle}>회수 결과</div>
                                <div style={rewardRow}>
                                    <span style={rewardIcon}>🪨</span>
                                    <span>장인의 재 × <strong>{result.artisanAshGained}</strong></span>
                                </div>
                                {result.refineStoneGained > 0 && (
                                    <div style={rewardRow}>
                                        <span style={rewardIcon}>⛏</span>
                                        <span>
                                            {result.refineStoneId.endsWith("rare") ? "별철" : "기본 제련석"} × <strong>{result.refineStoneGained}</strong>
                                        </span>
                                    </div>
                                )}
                                {result.pityTokenBonus > 0 && (
                                    <div style={rewardRow}>
                                        <span style={rewardIcon}>🕊</span>
                                        <span>연민의 증표 × <strong>{result.pityTokenBonus}</strong></span>
                                    </div>
                                )}
                            </div>
                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                <PrimaryButton onClick={finish}>닫기</PrimaryButton>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
}

// ─── styles ──────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1500,
};
const modalStyle: React.CSSProperties = {
    background: tokens.color.ink, color: tokens.color.parchment,
    padding: 26, borderRadius: 12, minWidth: 420, maxWidth: 540,
    border: `1px solid ${tokens.color.burgundy}77`,
    boxShadow: `0 16px 60px rgba(0,0,0,0.5), 0 0 24px ${tokens.color.burgundy}22`,
};
const titleStyle: React.CSSProperties = {
    fontSize: 22, margin: "0 0 12px", color: tokens.color.parchment, fontFamily: "serif",
};
const rewardBox: React.CSSProperties = {
    padding: 14, borderRadius: 8,
    background: `${tokens.color.brass}12`,
    border: `1px solid ${tokens.color.brass}44`,
    marginBottom: 14,
};
const rewardTitle: React.CSSProperties = {
    fontSize: 11, opacity: 0.7, letterSpacing: 2, marginBottom: 8, color: tokens.color.brass,
};
const rewardRow: React.CSSProperties = {
    display: "flex", alignItems: "center", gap: 10,
    fontSize: 15, padding: "4px 0",
};
const rewardIcon: React.CSSProperties = {
    fontSize: 20, width: 28, textAlign: "center",
};
const warningText: React.CSSProperties = {
    fontSize: 12, opacity: 0.75, fontStyle: "italic", margin: "10px 0 14px",
    color: tokens.color.ember,
};
const buttonRow: React.CSSProperties = {
    display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap",
};
const secondaryBtn: React.CSSProperties = {
    padding: "10px 18px", fontSize: 13,
    background: "transparent", color: tokens.color.parchment,
    border: `1px solid ${tokens.color.brass}55`, borderRadius: 6, cursor: "pointer",
};
